// src/shared/attachments-ui.js — the client half of "SEVERAL reference files".
//
// v129.113. Four reports on 13 Aug (three Head Office colleagues, one of them twice) said the same
// thing about composing a task or a news post: one file is not enough. The cap
// was in the schema (see migrations/v195-0-multi-attachments.sql), but the UI
// half is here — a picker that accumulates instead of replacing, and a reader
// that shows every file rather than only the first.
//
// Why shared: the task composer and the news composer had already grown
// byte-identical copies of `resolveMime` and near-identical ingest functions.
// Making them BOTH multi-file would have made that two divergent copies of
// something harder. So the duplication ends here, in the same PR.
//
// The MAX lives on the server too (attachments.js). This copy is the UI's own
// guard so a person is told "3 maximum" while picking, rather than after a 6 MB
// upload; the server is still the one that decides.

import { openStoredFile } from './open-file.js';

export const MAX_COMPOSE_FILES = 3;
export const MAX_FILE_BYTES = 6 * 1024 * 1024;

// The allow-list, and the reason it exists: browsers report .docx/.xlsx/.csv
// inconsistently (sometimes '', sometimes application/octet-stream), so a file's
// own `type` is not trustworthy — fall back to its extension. Kept in step with
// TASK_ATTACHMENT_TYPES in src/worker/tasks.js, which has the final say.
const ALLOW = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif',
  'application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain', 'text/csv'];

const EXT_MIME = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
  pdf: 'application/pdf', doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv', txt: 'text/plain',
};

// v129.118 — is this a type we accept? The FILE PICKER has always been filtered
// by its `accept=` attribute, but a DRAGGED file bypasses that entirely, so the
// check has to live in code as well. The server still has the final say
// (TASK_ATTACHMENT_TYPES); this is so the person is told at the moment they drop
// it, rather than after they press Send.
export function isAllowedAttachType(mime) {
  return ALLOW.indexOf(String(mime || '').toLowerCase()) >= 0;
}

export function resolveAttachMime(f) {
  var t = String((f && f.type) || '').toLowerCase();
  if (ALLOW.indexOf(t) >= 0) return t;
  var ext = String((f && f.name) || '').split('.').pop().toLowerCase();
  return EXT_MIME[ext] || t || 'application/octet-stream';
}

function escAttr(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Read ONE file to a data URL. Resolves to null (never rejects) with a reason on
// `.note`, because a picker that throws mid-loop would silently lose the files
// after it. Deliberately NOT parallel at the call site: three files decoded at
// once on a shop laptop is how you get a stalled tab.
export function readAttachmentFile(f, fallbackName) {
  return new Promise(function (resolve) {
    if (!f) { resolve(null); return; }
    if (f.size > MAX_FILE_BYTES) {
      resolve({ error: (f.name || 'That file') + ' is too large (max 6 MB)' });
      return;
    }
    // v129.118 — refuse a type we cannot store, BY NAME, before reading 6 MB of
    // it. Matters most for drag-and-drop, which has no `accept=` to filter it.
    var mimeEarly = resolveAttachMime(f);
    if (!isAllowedAttachType(mimeEarly)) {
      resolve({ error: (f.name || 'That file') + ' is not a type we can attach (photo, PDF, Word, Excel, CSV or text)' });
      return;
    }
    var reader = new FileReader();
    reader.onload = function () {
      var mime = resolveAttachMime(f);
      var b64 = String(reader.result || '').split(',')[1] || '';
      if (!b64) { resolve({ error: 'Could not read ' + (f.name || 'that file') }); return; }
      resolve({
        filename: f.name || fallbackName || 'attachment',
        content_type: mime,
        data: 'data:' + mime + ';base64,' + b64,
      });
    };
    reader.onerror = function () { resolve({ error: 'Could not read ' + (f.name || 'that file') }); };
    reader.readAsDataURL(f);
  });
}

// Add files to a list, one at a time, up to the cap. Returns the new list and a
// note when something was refused — the caller shows the note. Refusing loudly
// beats truncating quietly: a quiet drop is the bug this whole change fixes.
export async function addAttachmentFiles(existing, fileList, fallbackName) {
  var list = (existing || []).slice();
  var files = fileList ? Array.prototype.slice.call(fileList) : [];
  var notes = [];
  for (var i = 0; i < files.length; i++) {
    if (list.length >= MAX_COMPOSE_FILES) {
      notes.push('Only ' + MAX_COMPOSE_FILES + ' files can be attached — ' + (files[i].name || 'the rest') + ' was not added');
      break;
    }
    var one = await readAttachmentFile(files[i], fallbackName);
    if (!one) continue;
    if (one.error) { notes.push(one.error); continue; }
    list.push(one);
  }
  return { list: list, note: notes.join('. ') || null };
}

// v136.71 — a refused file must not slip past Send. A Head Office colleague picked an
// Excel file over 6 MB for a task: the refusal was one grey line under the picker, she
// pressed Save, and the stores got the task with no file. The composer keeps the
// refusal until a later pick replaces it, paints it in red, and asks before sending.
export function paintFileState(el, count, refused) {
  if (!el) return;
  if (refused) {
    el.textContent = (count ? count + ' attached. ' : '') + 'NOT attached: ' + refused;
    el.style.color = 'var(--adm-red-text)';
    el.style.fontWeight = '700';
  } else {
    el.textContent = count ? (count + ' attached') : '';
    el.style.color = 'var(--tiny)';
    el.style.fontWeight = '';
  }
}
export function refusedSendOk(refused, confirmFn) {
  if (!refused) return true;
  var ask = confirmFn || (typeof confirm === 'function' ? confirm : null);
  if (!ask) return true;
  return !!ask('NOT attached: ' + refused + '.\n\nSend without it? Press Cancel to attach a smaller file '
    + '(saving a spreadsheet as a PDF, or removing its pictures, usually brings it under 6 MB).');
}

// The composer's chip row — one chip per picked file with an ✕. `removeFn` is
// the bundle's own global handler name, e.g. 'TasksAdmin.attachRemove'.
export function composeChipsHtml(list, removeFn) {
  var items = list || [];
  if (!items.length) return '';
  return '<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px">' + items.map(function (a, i) {
    return '<span style="display:inline-flex;align-items:center;gap:6px;background:var(--chip,var(--surface-3));'
      + 'border:1px solid var(--border-strong);border-radius:14px;padding:3px 8px;font-size:11.5px;max-width:220px">'
      + '<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">📎 ' + escAttr(a.filename || 'file') + '</span>'
      + '<span onclick="' + escAttr(removeFn) + '(' + i + ')" title="Remove" '
      + 'style="cursor:pointer;font-weight:800;color:var(--adm-red-text)">✕</span></span>';
  }).join('') + '</div>';
}

// The READER's links — one clickable name per attachment, from the metadata the
// list endpoint already sends. `openFn` is called with (itemId, index).
//
// Falls back to the pre-v129.113 singular fields when `attachments` is absent,
// so an app talking to a Worker that has not been deployed yet still shows the
// one file rather than nothing.
export function attachLinksHtml(item, openFn, opts) {
  var o = opts || {};
  var list = (item && Array.isArray(item.attachments) && item.attachments.length)
    ? item.attachments
    : ((item && item.has_attachment) ? [{ filename: item.attachment_name || 'attachment' }] : []);
  if (!list.length) return '';
  var cls = o.className ? ' class="' + escAttr(o.className) + '"' : '';
  var colour = o.colour || 'var(--brand,#009E3D)';
  var prefix = o.prefix || '';
  return '<div' + cls + '>📎 ' + list.map(function (a, i) {
    return '<span onclick="' + escAttr(openFn) + '(' + Number(item.id) + ',' + i + ')" '
      + 'style="color:' + colour + ';font-weight:600;cursor:pointer;text-decoration:underline">'
      + escAttr(prefix + (a.filename || 'attachment')) + '</span>';
  }).join('<span style="color:var(--tiny,#8696a0)"> · </span>') + '</div>';
}

// v129.118 (Head Office, 12 Aug: "News, Task & survey attached multiple files and DRAG
// FILES NOT WORKING"). Two asks in one report — v129.113 did the multiple-files
// half; this is the other half, which was never built at all.
//
// Wired on the composer's PERSISTENT root element, not on the compose box: each
// admin bundle re-renders by setting `root.innerHTML`, so anything bound to the
// box itself is thrown away on the next repaint. The root survives, so this binds
// ONCE (guarded by a data attribute) and keeps working through every re-render.
//
// Deliberately NOT a document-level listener: admin.html already has five inline
// drop zones of its own (staff / budget / add-on / daily / historical CSV
// uploads), and a global handler that preventDefaults would interfere with them.
export function wireDropZone(root, opts) {
  var o = opts || {};
  if (!root || typeof root.addEventListener !== 'function') return false;
  if (root.getAttribute('data-attach-drop') === '1') return false;   // already wired
  if (typeof o.onFiles !== 'function') return false;
  root.setAttribute('data-attach-drop', '1');

  var box = function () { return o.boxSelector ? root.querySelector(o.boxSelector) : root; };
  // Only react to a drag carrying FILES — dragging selected text or a link
  // across the page must not light the composer up or swallow the drop.
  var hasFiles = function (e) {
    var t = e && e.dataTransfer && e.dataTransfer.types;
    if (!t) return false;
    for (var i = 0; i < t.length; i++) if (String(t[i]) === 'Files') return true;
    return false;
  };
  var depth = 0;                     // dragenter/leave fire per child; count them
  // Set the three outline properties individually rather than the `outline`
  // shorthand: a shorthand carrying a CSS var() is normalised inconsistently
  // (and unreadably) by browsers, which also made it untestable.
  var highlight = function (on) {
    var b = box();
    if (!b) return;
    b.style.outlineStyle = on ? 'dashed' : '';
    b.style.outlineWidth = on ? '2px' : '';
    b.style.outlineColor = on ? 'var(--brand,#009E3D)' : '';
    b.style.outlineOffset = on ? '4px' : '';
  };
  root.addEventListener('dragenter', function (e) {
    if (!box() || !hasFiles(e)) return;
    depth++;
    highlight(true);
  });
  root.addEventListener('dragover', function (e) {
    if (!box() || !hasFiles(e)) return;
    e.preventDefault();                                  // required, or the drop never fires
    try { e.dataTransfer.dropEffect = 'copy'; } catch (err) { /* older browsers */ }
  });
  root.addEventListener('dragleave', function (e) {
    if (!hasFiles(e)) return;
    depth = Math.max(0, depth - 1);
    if (depth === 0) highlight(false);
  });
  root.addEventListener('drop', function (e) {
    var b = box();
    if (!b || !hasFiles(e)) return;
    e.preventDefault();                                  // else the browser NAVIGATES to the file
    depth = 0;
    highlight(false);
    var files = e.dataTransfer && e.dataTransfer.files;
    if (files && files.length) o.onFiles(files);
  });
  return true;
}

// Open attachment `index` out of an /api/*/attachment response. Understands both
// the v129.113 shape (`attachments: [...]`) and the older singular one, so a
// cached bundle and a current Worker (or the reverse) still open the file.
export function openFromPayload(payload, index) {
  if (!payload) return null;
  var list = Array.isArray(payload.attachments) ? payload.attachments : [];
  var pick = list[index || 0];
  if (!pick && !index && payload.data) pick = payload;      // legacy singular
  if (!pick || !pick.data) return null;
  return openStoredFile(pick.data, pick.filename, pick.content_type);
}
